import { useLocation } from "react-router-dom";
import React, { useState, useEffect, useRef } from "react";

import axios from "axios";

// importing MUI
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import ShowChartIcon from "@mui/icons-material/ShowChart";

// importing common variables
import {
  defualtErrorMessage,
  graphColorList,
} from "../common/common-variables";
import { smaDefaultConfigurations,   indicatorsList as defaultIndicatorsList, } from "../common/common-indicator-defaults";
import { searchFilter } from "../common/common-helper-functions";
import { calSMA, calEMA, calVWAP } from "../common/common-indicator-functions";

import { createTheme, ThemeProvider, styled } from "@mui/material/styles";
import {
  getTickers,
  searchTickers,
  getMacroSecondaryMetrics,
  getMacroOhlcData,
} from "../apis/analysis-apis";
import {
  getBookmarkGroupers,
  toggleBookmark,
  getdashboardInfo,
  toggleFavoriteDashboard,
  getBookmarkStatus,
  favoriteDashboard,
  updateBookmark,
  createBookmarkGrouper,
} from "../apis/user-apis";
import SearchableSelectBox from "../components/searchableSelectBox";
import SelectBox from "../components/select-box";
import RadioItem from "../components/radio-item";
import DashboardHeader from "../components/dashboard-header";
import BookmarkModal from "../components/bookmark-modal";
import BookmarkGrouperModal from "../components/bookmark-grouper-modal";
import PreLoader from "../components/loader";
import SelectIndicatorModal from "../components/select-indicator-modal";
import IndicatorSettingsModal from "../components/indicator-settings-modal";

import { DatePicker, Radio, Space, notification } from "antd";

import {
  createChart,
  CrosshairMode,
  LineStyle,
  PriceLineSource,
} from "lightweight-charts";

import { priceData } from "../data/price-data";
import { lineData, lineData2 } from "../data/line-data";
import { volumeData } from "../data/volume-data";

import * as dfd from "danfojs";

import dayjs from "dayjs";

const { RangePicker } = DatePicker;

const defaltPrimaryMetric = [
  {
    label: "Apple Inc - AAPL - Common Stock - NASDAQ - USA",
    value: "Apple Inc - AAPL - Common Stock - NASDAQ - USA",
  },
];

const defaltSecondaryMetric = [
  {
    label: "Producer Price Index by Industry: Deep Sea Freight Transportation",
    value: "Producer Price Index by Industry: Deep Sea Freight Transportation",
  },
];

const primaryScaleList = [
  { label: "Regular", value: "primary_linear", title: "Series values." },
  {
    label: "1st Degree ROC",
    value: "1st_degree_change",
    title:
      "Rate of Change - (ROC) is the percentage change between the current value and the previous value.",
  },
  {
    label: "2nd Degree ROC",
    value: "2nd_degree_change",
    title: "Rate of Change of the Rate of Change.",
  },
  // {"label": "Log", "value": "primary_log", "title": "Log of the series values."},
  {
    label: "Change",
    value: "index_change",
    title: "Difference between the current value and the previous value.",
  },
  {
    label: "YOY Change",
    value: "index_change_from_year_ago",
    title:
      "Difference between the current value and the value on the same day/ week/ quater/ month in the previous year.",
  },
  {
    label: "YOY Change %",
    value: "percent_change_from_year_ago",
    title:
      "Percent Change between the current value and the value on the same day/ week/ quater/ month in the previous year.",
  },
  {
    label: "Cumulative Compounded Return",
    value: "returns",
    title:
      "Cumulative compounded return is the rate of return represents the cumulative effect that a series of gains or losses has on an original amount of capital over a period of time. It is calculated based on the daily returns of the investment.",
  },
  {
    label: "Normalise",
    value: "normalise",
    title:
      "Normalise the series so that it is easier to compare. Formula for nomalisation is (x - min(x))/(max(x) - min(x))",
  },
  {
    label: "Standardise",
    value: "Standardise",
    title:
      "Standardise the series so that it is easier to compare. Formula for standardisation is (x - mean(x))/std(x)",
  },
  { label: "Inverse", value: "Inverse", title: "Inverse of the series." },
];

const darkTheme = createTheme({ palette: { mode: "dark" } });

const Item = styled(Paper)(({ theme }) => ({
  ...theme.typography.body2,
  textAlign: "center",
  color: theme.palette.text.secondary,
  height: 60,
  lineHeight: "60px",
}));

const darkDatePickerStyle = {
  backgroundColor: "#000", // Set background color to black
  color: "#fff", // Set text color to white
};

const graphDateFormat = "YYYY-MM-DD";

export default function FreightRates() {
  // defining graph related states
  const [tickersOptions, setTickersOptions] = useState([]);
  const [selectPrimaryMetrics, setSelectPrimaryMetrics] =
    useState(defaltPrimaryMetric);
  const [selectSecondayMetrics, setSelectSecondayMetrics] = useState(
    defaltSecondaryMetric
  );
  const [graphDate, setGraphDate] = useState({
    startDate: "2015-01-01",
    endDate: "2024-01-01",
  });
  const [primayScale, setPrimaryScale] = useState({
    label: "Regular",
    value: "primary_linear",
    title: "Series values.",
  });
  const [secondaryScale, setSecondaryScale] = useState({
    label: "Regular",
    value: "primary_linear",
    title: "Series values.",
  });
  const [secondaryGraphType, setSecondaryGraphType] = useState("linear");
  const [secondaryMetricOptions, setSecondaryMetricOptions] = useState([]);
  const [dashboardTitle, setDashboardTitle] = useState("");
  const [primaryData, setPrimaryData] = useState([]);
  const [secondaryData, setSecondaryData] = useState([]);
  const [bookmarkFolders, setBookmarkFolders] = useState([]);
  const [hoverdValuesLegends, setHoverdValuesLegends] = useState([]);

  // defining the bookmark related states.
  const [bookmarkName, setBookmarkName] = useState("");
  const [bookmarkNameError, setBookmarkNameError] = useState("");
  const [bookmarkDescription, setBookmarkDescription] = useState("");
  const [bookmarkDescriptionError, setBookmarkDescriptionError] = useState("");
  const [selectedBookmarkFolder, setSelectedBookmarkFolder] = useState("0");
  const [bookmarkFolderError, setBookmarkFolderError] = useState("");
  const [isBookmarkPublic, setIsBookmarkPublic] = useState(false);
  const [isDashboardBookmarked, setIsDashboardBookmarked] = useState(false);
  const [isBookmarkModalOpen, setIsBookmarkModalOpen] = useState(false);

  //  Like related states
  const [isDashboardLiked, setIsDashboardLiked] = useState(false);

  // contains all the information about the dashboard
  const [dashboardInfo, setDashboardInfo] = useState({});

  // contains all the information about the bookmark
  const [bookmarkInfo, setBookmarkInfo] = useState({});

  // contains all the information about the bookmark form
  const [bookmarkFormInfo, setBookmarkFormInfo] = useState({});

  // states related to the bookmark grouper modal
  const [isBookmarkGrouperModalOpen, setIsBookmarkGrouperModalOpen] =
    useState(false);
  const [bookmarkGrouperInput, setBookmarkGrouperInput] = useState("");
  const [bookmarkGrouperInputError, setBookmarkGrouperInputError] =
    useState("");

  const [showLoader, setShowLoader] = useState(true);

  // indicator relatate state
  const [showIndicatorModal, setShowIndicatorModal] = useState(false);
  const [indicatorsList, setIndicatorsList] = useState(defaultIndicatorsList);
  const [selectedIndicatorsList, setSelectedIndicatorsList] = useState({});
  const [selectedIndicatorsSettings, setSelectedIndicatorsSettings] = useState(
    []
  );
  const [showIndicatorSettingsModal, setShowIndicatorsSettingsModal] =
    useState(false);
  
    const [color, setColor] =
    useState("#1677ff");

  const [indicatorSettingsModalHeader, setIndicatorSettingsModalHeader] =
    useState("");
  
    const [indicatorSettingsModalConfigurations, setIndicatorSettingsModalConfigurations] =
    useState(smaDefaultConfigurations);


  // ref
  const chartContainerRef = useRef();
  const chart = useRef();
  const tooltipRef = useRef();

  // setting the secondary metrics options on dashbaord load.
  const location = useLocation();

  // to open bookmark folder
  const handleOpenBookmarkModal = () => {
    setIsBookmarkModalOpen(true);
  };

  // to close bookmark folder
  const handleCloseBookmarkModal = () => {
    setIsBookmarkModalOpen(false);
  };

  // fetching the secondary metrics from dashboard
  useEffect(() => {
    const source = axios.CancelToken.source();
    const paramsObject = {
      dashboard_name: "producer_price_index_react_codes",
      dashboard_path: location.pathname,
      cancelToken: source.token,
    };
    getMacroSecondaryMetrics(paramsObject)
      .then((resp) => {
        const secondaryOptions = resp.data.data;
        setSecondaryMetricOptions(secondaryOptions);
        setDashboardTitle(resp.data.dashboard_title);
      })
      .catch((err) => {
        if (axios.isCancel(err)) {
          //console.log("request cancel successfully.")
        } else {
          //console.log("need to handel this error.", err)
        }
        ////console.log("this is the error", err)
      });

    return () => {
      //console.log('canceling the request')
      source.cancel("Request canceled by cleanup");
    };
  }, []);

  // to check if the dashboard is favorite of not.
  useEffect(() => {
    const source = axios.CancelToken.source();

    if (dashboardInfo.id) {
      const params = {
        dashboard_id: dashboardInfo.id,
        cancelToken: source.token,
      };
      favoriteDashboard(params)
        .then((resp) => {
          // updating the dashoard like status
          setIsDashboardLiked(resp.data.is_dashboard_liked);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            //console.log("request cancel successfully.")
          } else {
            //console.log("need to handel this error.", err)
          }
        });
    }

    return () => {
      //console.log('canceling the request')
      source.cancel("Request canceled by cleanup");
    };
  }, [dashboardInfo.id]);

  // to update the bookmark status as per change in inputs.
  useEffect(() => {
    const source = axios.CancelToken.source();
    if (dashboardInfo.id) {
      const bookmarkParameters = getBookmarkParameters();
      const payload = {
        dashboard_id: dashboardInfo.id,
        bookmark_parameters: bookmarkParameters,
        cancelToken: source.token,
      };

      getBookmarkStatus(payload)
        .then((resp) => {
          // updating the dashboard title
          setDashboardTitle(resp.data.name);
          // updating the dashboard title
          setIsBookmarkPublic(resp.data.is_public ? true : false);
          // updating the dashboard title
          setIsDashboardBookmarked(resp.data.is_bookmarked ? true : false);

          // also updating the bookmark form

          setBookmarkName(resp.data.is_bookmarked ? resp.data.name : "");
          setBookmarkDescription(
            resp.data.is_bookmarked ? resp.data.description : ""
          );
          setSelectedBookmarkFolder(
            resp.data.is_bookmarked ? resp.data.grouper : "0"
          );
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            //console.log("request cancel successfully.")
          } else {
            //console.log("need to handel this error.", err)
          }
        });
    }

    return () => {
      //console.log('canceling the request')
      source.cancel("Request canceled by cleanup");
    };
  }, [
    dashboardInfo.id,
    selectPrimaryMetrics,
    selectSecondayMetrics,
    graphDate,
    primayScale,
    secondaryScale,
  ]);

  // fetching the dashboard info
  useEffect(() => {
    const source = axios.CancelToken.source();

    const paramsObject = {
      dashboard_path: location.pathname,
      cancelToken: source.token,
    };
    getdashboardInfo(paramsObject)
      .then((resp) => {
        setDashboardInfo(resp.data);
      })
      .catch((err) => {
        if (axios.isCancel(err)) {
          //console.log("request cancel successfully.")
        } else {
          //console.log("need to handel this error.", err)
        }
      });
    return () => {
      //console.log('canceling the request')
      source.cancel("Request canceled by cleanup");
    };
  }, []);

  // fetching the bookmarks from parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    if (searchParams.size) {
      // setting the input fields according to the queryset parameters.

      const selectPrimaryMetrics = searchParams.has("primary_metric")
        ? JSON.parse(searchParams.get("primary_metric"))
        : selectPrimaryMetrics;
      const selectSecondayMetrics = searchParams.has("secondary_metric")
        ? JSON.parse(searchParams.get("secondary_metric"))
        : selectPrimaryMetrics;
      const startDate = searchParams.has("start_date")
        ? searchParams.get("start_date")
        : startDate;
      const endDate = searchParams.has("end_date")
        ? searchParams.get("end_date")
        : endDate;
      const primayScale = searchParams.has("primary_scale")
        ? JSON.parse(searchParams.get("primary_scale"))
        : primayScale;
      const secondaryScale = searchParams.has("secondary_scale")
        ? JSON.parse(searchParams.get("secondary_scale"))
        : secondaryScale;

      //console.log("this is the selectPrimaryMetrics", selectPrimaryMetrics)
      //console.log("this is the selectSecondayMetrics", selectSecondayMetrics)
      //console.log("this is the startDate", startDate)
      //console.log("this is the endDate", endDate)
      //console.log("this is the primayScale", primayScale)
      //console.log("this is the secondaryScale", secondaryScale)

      setSelectPrimaryMetrics(selectPrimaryMetrics);
      setSelectSecondayMetrics(selectSecondayMetrics);
      setGraphDate({ startDate: startDate, endDate: endDate });
      setPrimaryScale(primayScale);
      setSecondaryScale(secondaryScale);
    }

    const paramsObject = {
      dashboard_name: "producer_price_index_react_codes",
      dashboard_path: location.pathname,
    };

    getMacroSecondaryMetrics(paramsObject)
      .then((resp) => {
        const secondaryOptions = resp.data.data;
        setSecondaryMetricOptions(secondaryOptions);
        setDashboardTitle(resp.data.dashboard_title);
      })
      .catch((err) => {
        ////console.log("this is the error", err)
      });
  }, []);

  useEffect(() => {
    const paramsObject = {
      dashboard_name: "producer_price_index_react_codes",
      dashboard_path: location.pathname,
    };
    getMacroSecondaryMetrics(paramsObject)
      .then((resp) => {
        const secondaryOptions = resp.data.data;
        setSecondaryMetricOptions(secondaryOptions);
        setDashboardTitle(resp.data.dashboard_title);
      })
      .catch((err) => {
        //console.log("this is the error", err)
      });
  }, []);

  // fetching the bookmark folders.
  useEffect(() => {
    getBookmarkGroupers()
      .then((resp) => {
        setBookmarkFolders(resp.data);
        //console.log("this are the bookmark folders")
      })
      .catch((err) => {
        //console.log("this is the error while fetching bookmarks folders", err)
      });
  }, []);

  // setting the secondary metrics options on dashbaord load.
  useEffect(() => {
    // console.log("setting the show loader true");
    setShowLoader(true);

    const primaryMetrics = selectPrimaryMetrics?.map((metric) => metric.value);
    const secondaryMetrics = selectSecondayMetrics?.map(
      (metric) => metric.value
    );
    const startDate = graphDate.startDate;
    const endDate = graphDate.endDate;

    const queryObject = {
      primary_metrics: primaryMetrics,
      secondary_metrics: secondaryMetrics,
      start_date: startDate,
      end_date: endDate,
    };

    getMacroOhlcData(queryObject)
      .then((resp) => {
        // const df = new dfd.DataFrame(data);
        setPrimaryData(JSON.parse(resp.data.primary_data));
        setSecondaryData(JSON.parse(resp.data.secondary_data));
        // console.log("setting the show loader false");
        setShowLoader(false);

        // df.head().print()
      })
      .catch((err) => {
        //console.log("this is the error", err)
      });

    const data = {
      key1: "value1",
      key2: "value2",
    };

    // axios.post('http://localhost:8000/analysis/post/', data).then((resp)=>{
    //   ////console.log("this is the response for post req", resp.data);
    // }).catch((err)=>{
    //   ////console.log("this is the error", err)
    // })
  }, [selectPrimaryMetrics, selectSecondayMetrics, graphDate]);

  useEffect(() => {
    //console.log("regenerating the chart agin", primaryData)
    chart.current = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: chartContainerRef.current.clientHeight - 20, //'800', //"300px", //chartContainerRef.current.clientHeight,
      // chart color options:   https://tradingview.github.io/lightweight-charts/tutorials/customization/chart-colors
      layout: {
        backgroundColor: "#000",
        textColor: "rgba(255, 255, 255, 0.9)",
      },
      grid: {
        vertLines: {
          color: "#334158",
        },
        horzLines: {
          color: "#334158",
        },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
      },
      priceScale: {
        borderColor: "#485c7b",
      },
      timeScale: {
        borderColor: "#485c7b",
      },
      watermark: {
        visible: true,
        fontSize: 40,
        horzAlign: "center",
        vertAlign: "center",
        color: " rgba(255, 165, 0, 0.1)",
        text: "Anahit",
      },
    });

    chart.current.subscribeClick(myClickHandler);

    
    // const watermark = new Image();
    // watermark.src = 'https://anahit.ai/static/img/images/Anahit-logo.png';

    // // Set the watermark styles
    // watermark.style.position = 'absolute';
    // watermark.style.opacity = '0.1'; // Adjust the opacity as needed
    // watermark.style.zIndex = '10'; // Make sure the watermark appears above the chart

    // // Append the watermark to the chart container
    // document.getElementById('chart-id').appendChild(watermark);

    // Setting the border color for the vertical axis
    // chart.current.priceScale('right').applyOptions({
    //     borderColor: '#71649C',
    //     visible:true
    // });

    // Setting the border color for the horizontal axis
    chart.current.timeScale("left").applyOptions({
      borderColor: "#71649C",
      visible: true,
    });

    chart.current.priceScale("left").applyOptions({
      // borderColor: '#71649C',
      visible: true,
    });

    ////console.log(chart.current);

    const candleSeries = chart.current.addCandlestickSeries({
      upColor: "#4bffb5",
      downColor: "#ff4976",
      borderDownColor: "#ff4976",
      borderUpColor: "#4bffb5",
      wickDownColor: "#838ca1",
      wickUpColor: "#838ca1",
    });

    candleSeries.applyOptions({
      priceScaleId: "left",
    });

    // candleSeries.setData(priceData);

    // const areaSeries = chart.current.addAreaSeries({
    //   topColor: 'rgba(38,198,218, 0.56)',
    //   bottomColor: 'rgba(38,198,218, 0.04)',
    //   lineColor: 'rgba(38,198,218, 1)',
    //   lineWidth: 2
    // });

    // areaSeries.setData(areaData);

    const volumeSeries = chart.current.addHistogramSeries({
      color: "green",
      lineWidth: 2,
      priceFormat: {
        type: "volume",
      },
      overlay: true,
      scaleMargins: {
        top: 0.8,
        bottom: 0,
      },
    });

    // volumeSeries.setData(volumeData);

    if (secondaryData?.length) {
      ////console.log("insdie primary count df", secondaryScale)
      const secondaryValueToBeShown =
        secondaryScale.value == "primary_linear"
          ? "value"
          : secondaryScale.value;
      secondaryData.forEach(function (object, index) {
        const code = Object.keys(object)[0];
        //console.log("this are the seondary key ", code)
        const lineSeriesSecondary = chart.current.addLineSeries({
          color: graphColorList[index],
        }); //, priceScaleId: "left", lineStyle: LineStyle.Solid, lineWidth: 1, priceLineSource: PriceLineSource.LastVisible
        const secondaryParsedata = object[code]?.map((df) => ({
          time: df.date, // timestamp, Ex: 1698361200000
          value: df[secondaryValueToBeShown],
        }));
        lineSeriesSecondary.setData(secondaryParsedata);
      });
    } else {
      // lineSeriesPrimary.setData(lineData2);
    }

    if ((Object.keys(selectedIndicatorsList)?.length) && (primaryData?.length)) {
      let newData = primaryData
      
      const source =
        primayScale.value == "primary_linear"
          ? "adjusted_close"
          : primayScale.value;

      const code = Object.keys(primaryData[0])[0]

      Object.keys(selectedIndicatorsList).forEach(function (indicator) {
      
        console.log("indicator", indicator)
      
      if(indicator === "sma"){
        newData = calSMA(primaryData, source, selectedIndicatorsList[indicator]["period"], code)
        var value = `${indicator}${selectedIndicatorsList[indicator]["period"]}`
        setPrimaryData(newData)
      }
      if(indicator === "ema" ){
        newData = calEMA(primaryData, source, selectedIndicatorsList[indicator]["period"], code)
        var value = `${indicator}${selectedIndicatorsList[indicator]["period"]}`
        setPrimaryData(newData)
      }
      
      if(indicator === "vwap" ){
        newData = calVWAP(primaryData, code)
        var value = indicator
        setPrimaryData(newData)
      }


      newData.forEach(function (object, index) {
        const code = Object.keys(object)[0];
        //console.log("this are the seondary key ", code)
        const lineSeriesIndicator = chart.current.addLineSeries({
          color: selectedIndicatorsList[indicator]["style"]["color"],
        }); //, priceScaleId: "left", lineStyle: LineStyle.Solid, lineWidth: 1, priceLineSource: PriceLineSource.LastVisible

        
        console.log("value", value)
        const indicatorData = object[code]?.map((df) => ({
          time: df.date, // timestamp, Ex: 1698361200000
          value: df[value] ,
        }));
        lineSeriesIndicator.setData(indicatorData);
        lineSeriesIndicator.applyOptions({
          priceScaleId: "left",
        })
      });
    });
    } else {
      // lineSeriesPrimary.setData(lineData2);
    }

    ////console.log("primary data primaryData.count", primaryData.count)
    var lineSeriesPrimary = null;
    if (primaryData?.length) {
      const primaryValueToBeShown =
        primayScale.value == "primary_linear"
          ? "adjusted_close"
          : primayScale.value;
      primaryData.forEach(function (object, index) {
        const code = Object.keys(object)[0];
        //console.log("this is the primaryData key", code)

        lineSeriesPrimary = chart.current.addLineSeries({
          color: graphColorList[index + selectSecondayMetrics.length],
        });
        const dataParsed = object[code]?.map((df) => ({
          time: df.date,
          value: df[primaryValueToBeShown],
        }));
        lineSeriesPrimary.setData(dataParsed);
        lineSeriesPrimary.applyOptions({
          priceScaleId: "left",
        });
      });
    } else {
      // lineSeriesPrimary.setData(lineData2);
    }

    // lineSeriesSecondary.leftPriceScale: {
    //   visible: true,
    //   mode: PriceScaleMode.Percentage,
    //   borderVisible: false,
    // },
    // lineSeriesSecondary.applyOptions({
    //     priceScaleId: 'right'
    // });

    // to zoomin 100% on load
    chart.current.timeScale().fitContent();

    // Customizing the Crosshair
    // https://tradingview.github.io/lightweight-charts/tutorials/how_to/two-price-scales
    chart.current.applyOptions({
      crosshair: {
        // Change mode from default 'magnet' to 'normal'.
        // Allows the crosshair to move freely without snapping to datapoints
        // mode: CrosshairMode.Normal,

        // Vertical crosshair line (showing Date in Label)
        vertLine: {
          // width: 8,
          // color: '#C3BCDB44',
          // style: LineStyle.Solid,
          labelBackgroundColor: "#9B7DFF",
        },

        // Horizontal crosshair line (showing Price in Label)
        horzLine: {
          // color: '#9B7DFF',
          labelBackgroundColor: "#9B7DFF",
        },
        rightPriceScale: {
          visible: false,
        },
        leftPriceScale: {
          visible: true,
        },
      },
    });

    // gets triggered whenever the mouse hover overs graph
    chart.current.subscribeCrosshairMove((params) => {
      // console.log("params", params)
      if (params.time) {
        // console.log("params point", params.point)
        const hoverdValues = [];
        const tooltipLeft = params.point.x;
        const data = params.seriesPrices.get(lineSeriesPrimary);
        const tooltipTop = lineSeriesPrimary.priceToCoordinate(data);

        tooltipRef.current.style.left = tooltipLeft + "px";
        // tooltipRef.current.style.top = tooltipTop + 20 + "px"

        // console.log("this is the tooltipLeft", tooltipLeft)
        // console.log("this is the tooltipTop", tooltipTop)

        for (let [key, value] of params.seriesPrices.entries()) {
          //console.log(`key : ${key}, value :${value}`);
          // console.dir(`key dir : ${key}`);x
          hoverdValues.push(value);
          setHoverdValuesLegends(hoverdValues);
        }
      } else {
        setHoverdValuesLegends([]);
      }
    });

    // setting markers for the chart.
    const markers = [
      {
        time: priceData[priceData.length - 20].time,
        position: "aboveBar",
        color: "#fff",
        shape: "circle",
        text: "sell",
      },
    ];
    ////console.log("markers", markers);
    candleSeries.setMarkers(markers);

    chartContainerRef.current.clientWidth;

    const handleWindowResize = () => {
      // console.log(
      //   `reseting the graph width: ${chartContainerRef.current.clientWidth} height: ${chartContainerRef.current.clientHeight}`
      // );
      chart.current.applyOptions({
        width: chartContainerRef.current.clientWidth,
        height: chartContainerRef.current.clientHeight - 20,
      });
    };

    window.addEventListener("resize", handleWindowResize);

    return () => {
      chart.current.remove();
      window.removeEventListener("resize", handleWindowResize);
    };
  }, [
    primaryData,
    secondaryData,
    primayScale,
    secondaryScale,
    selectedIndicatorsList,
  ]);

  const handleTickerSearchInput = (inputValue, callback) => {
    // Handle changes in the search input
    const queryStirng = {};
    queryStirng["primary_metric"] = inputValue;
    searchTickers(queryStirng)
      .then((resp) => {
        ////console.log("this is the response for searchTickers", resp);
        let tickersList = resp.data.data;
        // const tickersNames = tickersList.map((item) => ({
        //   value: item.code,
        //   label: item.name,
        // }));
        setTickersOptions(tickersList);
        callback(tickersList);
      })
      .catch((err) => {
        ////console.log("this is the errror", err);
      });
  };

  const handleSelectChangePrimary = (selectedOptions) => {
    ////console.log("this are the selected primary items",selectedOptions )
    setSelectPrimaryMetrics(selectedOptions);
  };

  const handleSelectChangeSecondary = (selectedOptions) => {
    ////console.log("this are the selected secondary items", selectedOptions)
    setSelectSecondayMetrics(selectedOptions);
  };

  const handleScaleChangePrimary = (selectedOptions) => {
    ////console.log("this are the selected primary items",selectedOptions )
    setPrimaryScale(selectedOptions);
  };

  const handleScaleChangeSecondary = (selectedOptions) => {
    ////console.log("this are the selected secondary items", selectedOptions)
    setSecondaryScale(selectedOptions);
  };

  const handleChangeDate = (selectedDate) => {

    const startDate = selectedDate[0].format('YYYY-MM-DD');
    const endDate = selectedDate[1].format('YYYY-MM-DD');

    setGraphDate({
      startDate: startDate,
      endDate: endDate,
    })
  };

  const handleChangeSecondaryMetric = (selectedOptions) => {
    //console.log("INsiede the secondary handle change", selectedOptions)
    setSelectSecondayMetrics(selectedOptions);
  };

  const handleClickLike = (event) => {
    // handle the like action
    toggleLikeAction();
  };

  const handleClickBookmark = (event) => {
    // if dashboard is already bookamrked removing the bookmark else creating new bookmark.
    if (isDashboardBookmarked) {
      //
      togglebookmarkAction();
    } else {
      setIsBookmarkModalOpen(true);
    }
  };

  const handleClickBookmarkEdit = (event) => {
    // showing the bookmark edit modal
    setIsBookmarkModalOpen(true);
  };

  // bookmark functionality.
  const handleBookmarkNameChange = (event) => {
    //console.log("setting the bookamrk name", event.target.value)
    setBookmarkName(event.target.value);
    setBookmarkNameError("");
  };

  const handleBookmarkDescriptionChange = (event) => {
    //console.log("setting the bookamrk description", event.target.value)
    setBookmarkDescription(event.target.value);
  };

  const handleChangeBookmarkFolder = (event) => {
    //console.log("setting the bookamrk folder", event.target.value)
    setSelectedBookmarkFolder(event.target.value);
    setBookmarkFolderError("");
    if (event.target.value === "0") {
      handleCloseBookmarkModal();
      showBookmarkGroouperModal();
    }
  };

  const handleChangeIsBookmarkPublic = (event) => {
    //console.log("setting the bookamrk ispublic", event.target.checked)
    setIsBookmarkPublic(event.target.checked);
  };

  const handleBookmarkSubmit = () => {
    //console.log("handeling bookmark submit")
    if (validateBookmark()) {
      isDashboardBookmarked ? updateBookmarkAction() : togglebookmarkAction();
    }
  };

  const validateBookmark = () => {
    // validates the mandatory fields while creating bookmark shows the proper message if not valid.

    let isValid = true;
    if (!bookmarkName?.trim()) {
      isValid = false;
      //console.log("")
      setBookmarkNameError("Please enter the name for bookmark.");
    }

    //console.log("typeof selectedBookmarkFolder", typeof selectedBookmarkFolder)
    //console.log("selectedBookmarkFolder", selectedBookmarkFolder)
    if (selectedBookmarkFolder === "0") {
      isValid = false;
      setBookmarkFolderError("Please select the folder for the bookmark.");
    }

    return isValid;
  };

  const togglebookmarkAction = () => {
    const bookmarkParameters = getBookmarkParameters();

    const data = {
      parameters: bookmarkParameters,
      dashboard_id: dashboardInfo.id, //need to add the dashboard id here.
      description: bookmarkDescription,
      name: bookmarkName,
      is_bookmark_public: isBookmarkPublic,
      bookmark_grouper_id: selectedBookmarkFolder,
    };

    toggleBookmark(data)
      .then((resp) => {
        // change the dashboard name to the bookmark name
        setDashboardTitle(resp.data.bookmark_name);

        // change the dashboard description to the bookmark description

        // make bookmark icon active
        setIsDashboardBookmarked(
          resp?.data?.is_bookmark_created ? true : false
        );

        // update the bookmark public flag.
        setIsBookmarkPublic(resp?.data?.is_bookmark_public ? true : false);

        // show the edit bookmark icon
        setIsBookmarkModalOpen(false);

        // if bookmark is removed reseting the input fields
        if (!resp.data.is_bookmark_created) {
          setBookmarkName("");
          setBookmarkDescription("");
          setIsBookmarkPublic(false);
        }

        // showing the notification message
        notification.success({
          message: "Done!",
          description: resp.data.message,
          placement: "bottomRight",
        });
      })
      .catch((err) => {
        notification.error({
          message: "Error",
          description: err.response.data?.message || defualtErrorMessage,
          placement: "bottomRight",
        });
        //console.log("this is the errror for toggle bookmark", err)
      });
  };

  const updateBookmarkAction = () => {
    //console.log("updating the bookamrk")
    const bookmarkParameters = getBookmarkParameters();

    const data = {
      bookmark_parameters: bookmarkParameters,
      dashboard_id: dashboardInfo.id, //need to add the dashboard id here.
      bookmark_description: bookmarkDescription,
      bookmark_name: bookmarkName,
      is_bookmark_public: isBookmarkPublic,
      bookmark_grouper_id: selectedBookmarkFolder,
    };

    updateBookmark(data)
      .then((resp) => {
        // change the dashboard name to the bookmark name
        setDashboardTitle(resp.data.bookmark_name);

        // change the dashboard description to the bookmark description

        // make bookmark icon active
        // setIsDashboardBookmarked(resp?.data?.is_bookmark_created ? true : false)

        // update the bookmark public flag.
        // setIsBookmarkPublic(resp?.data?.is_bookmark_public ? true : false)

        // show the edit bookmark icon
        setIsBookmarkModalOpen(false);

        // showing the notification message
        notification.success({
          message: "Done!",
          description: resp.data.message,
          placement: "bottomRight",
        });
      })
      .catch((err) => {
        notification.error({
          message: "Error",
          description: err.response.data?.message || defualtErrorMessage,
          placement: "bottomRight",
        });
        //console.log("this is the errror for toggle bookmark", err)
      });
  };

  const toggleLikeAction = () => {
    const favoriteDashboardData = {};
    favoriteDashboardData.task = !isDashboardLiked;
    favoriteDashboardData.dashboard = dashboardInfo.id;
    toggleFavoriteDashboard(favoriteDashboardData)
      .then((resp) => {
        // changing the like icon.
        setIsDashboardLiked((prevState) => !prevState);
        notification.success({
          message: "Done!",
          description: resp.data.message,
          placement: "bottomRight",
        });
      })
      .catch((err) => {
        //console.log("this is the errror for toggle bookmark", err)
        notification.error({
          message: "Error",
          description: err.response.data?.message || defualtErrorMessage,
          placement: "bottomRight",
        });
      });
  };

  const getBookmarkParameters = () => {
    const startDate = graphDate.startDate;
    const endDate = graphDate.endDate;

    const bookmarkParameters = `${
      dashboardInfo?.link
    }?primary_metric=${encodeURIComponent(
      JSON.stringify(selectPrimaryMetrics)
    )}&secondary_metric=${encodeURIComponent(
      JSON.stringify(selectSecondayMetrics)
    )}&start_date=${encodeURIComponent(
      startDate
    )}&end_date=${encodeURIComponent(
      endDate
    )}&primary_scale=${encodeURIComponent(
      JSON.stringify(primayScale)
    )}&secondary_scale=${encodeURIComponent(JSON.stringify(secondaryScale))}`;

    return bookmarkParameters;
  };

  const handelClickBookmarkGrouper = (event) => {
    // check if the form is field
    if (validateBookmarkGrouper()) {
      // making the api call for creating new bookmark grouper

      const payload = { bookmark_grouper_name: bookmarkGrouperInput.trim() };
      createBookmarkGrouper(payload)
        .then((resp) => {
          // hide the grouper modal
          setIsBookmarkGrouperModalOpen(false);

          // Add the newly created grouper to the folder
          const newGrouper = resp.data;
          setBookmarkFolders((prev) => [...prev, newGrouper]);

          //  selecting the newly created created folder
          setSelectedBookmarkFolder(newGrouper.id);

          // showing the notification that new bookmark modal is created successfully.
          notification.success({
            message: "Success!",
            description: "New folder is created Successfully!",
            placement: "bottomRight",
          });

          // show the bookmark modal select the newly created
          setIsBookmarkModalOpen(true);
        })
        .catch((err) => {
          notification.error({
            message: "Error",
            description: err.response.data?.message || defualtErrorMessage,
            placement: "bottomRight",
          });
        });
    }
  };

  const handleChangeBookmarkGrouperInput = (event) => {
    setBookmarkGrouperInput(event.target.value);
  };

  const handleCloseBookmarkGrouperModal = () => {
    setIsBookmarkGrouperModalOpen(false);
  };

  const showBookmarkGroouperModal = () => {
    setIsBookmarkGrouperModalOpen(true);
  };

  const validateBookmarkGrouper = () => {
    let isValid = true;

    if (!bookmarkGrouperInput?.trim()) {
      isValid = false;
      setBookmarkGrouperInputError("Please enter the name Folder Name!");
    }

    return isValid;
  };

  // Indicator Functionality :

  const handleClickShowIndicatorModal = () => {
    // opent the select Indicator modal
    console.log("showing the indicator modal");
    setShowIndicatorModal(true);
  };

  const handleCloseIndicatorModal = () => {
    // opent the select Indicator modal
    //console.log("closing the indicator modal");
    setShowIndicatorModal(false);
  };

  const handelClickSelectIndicator = (e) => {
    setShowIndicatorModal(false);
    setSelectedIndicatorsList((prev) => ({...prev, [e.target.innerText] : defaultIndicatorsList[e.target.innerText]["configurations"]}));
  };

  const handleChangeSearchTicker = (e) => {
    // opent the select Indicator modal
    const searchTerms = e.target.value;
    const filteredIndicators = searchFilter(searchTerms, defaultIndicatorsList);
    setIndicatorsList(filteredIndicators);
  };

  function myClickHandler(param) {
    //console.log(`Click at ${param}`);
    //console.log(JSON.stringify(param, null, 2));

  }
  
  const handleClickOpenIndicatorSettingsModal = (e) => {
      //console.log("handleClickOpenIndicatorSettingsModal", e?.target?.innerText)  
      setShowIndicatorsSettingsModal(true)
      if(e.target?.innerText){
        const settings = {
          period: selectedIndicatorsList[e.target.innerText]["period"],
          source: "adjusted_close",
          style: { color: selectedIndicatorsList[e.target.innerText]["style"]["color"] },
        };
        setIndicatorSettingsModalConfigurations(settings)
        setIndicatorSettingsModalHeader(e.target.innerText)
      }
  }
  
  const handleCloseIndicatorSettingsModal = (e) => {
    setShowIndicatorsSettingsModal(false)
  }

  const handelClickSaveIndicatorSetting = (e) => {

    let setting = indicatorSettingsModalConfigurations

    // console.log("setting", setting)
    // setting[indicatorSettingsModalHeader]["style"]["color"] = color
    // console.log("setting", setting)
    
    setShowIndicatorsSettingsModal(false)
    setSelectedIndicatorsList((prev) => ({...prev, [indicatorSettingsModalHeader] : setting}));
  };
  
  const handleChangeIndicatorConfigurationsColor = (e) => {
    //console.log("handleChangeIndicatorConfigurationsColor", e)
    setIndicatorSettingsModalConfigurations(prev => ({
      ...prev, 
      style: { ...prev.style, color: color }
    }));
  };
  const handleChangeIndicatorConfigurationsColorComplete = (e) => {
    //console.log("handleChangeIndicatorConfigurationsColorComplete", e)
    setIndicatorSettingsModalConfigurations(prev => ({
      ...prev, 
      style: { ...prev.style, color: color }
    }));
  };
  
  const handleChangeInputLength = (e) => {
    //console.log("handleChangeInputLength", e)
    setIndicatorSettingsModalConfigurations(prev => ({
      ...prev, 
      period: e
    }));
  };

  // console.log("selectedIndicatorsList", selectedIndicatorsList)
  return (
    <>
      <BookmarkModal
        isBookmarkModalOpen={isBookmarkModalOpen}
        handleCloseBookmarkModal={handleCloseBookmarkModal}
        bookmarkFolders={bookmarkFolders}
        bookmarkName={bookmarkName}
        bookmarkNameError={bookmarkNameError}
        handleBookmarkNameChange={handleBookmarkNameChange}
        bookmarkDescription={bookmarkDescription}
        handleBookmarkDescriptionChange={handleBookmarkDescriptionChange}
        selectedBookmarkFolder={selectedBookmarkFolder}
        bookmarkFolderError={bookmarkFolderError}
        handleChangeBookmarkFolder={handleChangeBookmarkFolder}
        isBookmarkPublic={isBookmarkPublic}
        handleChangeIsBookmarkPublic={handleChangeIsBookmarkPublic}
        handleBookmarkSubmit={handleBookmarkSubmit}
      />

      <BookmarkGrouperModal
        isModalOpen={isBookmarkGrouperModalOpen}
        handleCloseModal={handleCloseBookmarkGrouperModal}
        modalInputValue={bookmarkGrouperInput}
        modalInputError={bookmarkGrouperInputError}
        handleClick={handelClickBookmarkGrouper}
        handleChangeInput={handleChangeBookmarkGrouperInput}
      />

      <SelectIndicatorModal
        modalId="select-indicator-modal"
        open={showIndicatorModal}
        handleClose={handleCloseIndicatorModal}
        handleClickSelectIndicator={handelClickSelectIndicator}
        indicatorsList={indicatorsList}
        handleChangeInput={handleChangeSearchTicker}
      />
      <IndicatorSettingsModal
        // modalId = "select-indicator-modal"
        open={showIndicatorSettingsModal}
        handleClose = {handleCloseIndicatorSettingsModal}
        handleChangeIndicatorConfigurationsColor = {handleChangeIndicatorConfigurationsColor}
        handleChangeIndicatorConfigurationsColorComplete = {handleChangeIndicatorConfigurationsColorComplete}
        handleChangeInputLength = {handleChangeInputLength}
        header = {indicatorSettingsModalHeader}
        settings = {indicatorSettingsModalConfigurations}
        handelClickSaveIndicatorSetting = {handelClickSaveIndicatorSetting}
        setColor = {setColor}
        color = {color}
      />

      <ThemeProvider theme={darkTheme}>
        <Grid
          container
          item
          spacing={2}
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: "#000",
            display: "grid",
          }}
        >
          <DashboardHeader
            title={dashboardTitle}
            handleClickBookmark={handleClickBookmark}
            handleClickLike={handleClickLike}
            isDashboardLiked={isDashboardLiked}
            isDashboardBookmarked={isDashboardBookmarked}
            handleClickBookmarkEdit={handleClickBookmarkEdit}
          />
        </Grid>
        <Grid
          container
          item
          spacing={2}
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: "#000",
            display: "grid",
            gridTemplateColumns: { md: "1fr 1fr" },
            gap: 2,
          }}
        >
          <Grid item xs={12}>
            <Box
              sx={{
                // p: 2,
                borderRadius: 2,
                bgcolor: "#000",
                // display: 'grid',
                // gridTemplateColumns: { md: '1fr 1fr' },
                // gap: 2,
              }}
              className="select-container"
            >
              Primary Axis:
              <SearchableSelectBox
                isMulti = {true}
                selectedTickers={selectPrimaryMetrics}
                handleInputChange={handleTickerSearchInput}
                handleSelectChange={handleSelectChangePrimary}
                defaltPrimaryMetric={defaltPrimaryMetric}
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                // p: 2,
                borderRadius: 2,
                bgcolor: "#000",
                // display: 'grid',
                // gridTemplateColumns: { md: '1fr 1fr' },
                // gap: 2,
              }}
              className="select-container"
            >
              Secondary Axis
              {/* <SearchableSelectBox
                              tickersNames={tickersNames}
                              handleInputChange={handleTickerSearchInput}
                              handleSelectChange = {handleSelectChangeSecondary}
                              defaltPrimaryMetric = {defaltSecondaryMetric}
                          /> */}
              <SelectBox
                options={secondaryMetricOptions}
                defaultValue={[
                  {
                    label:
                      "Producer Price Index by Industry: Deep Sea Freight Transportation",
                    value:
                      "Producer Price Index by Industry: Deep Sea Freight Transportation",
                  },
                ]}
                isDisabled={false}
                isLoading={false}
                isClearable={true}
                isSearchable={true}
                handleSelectChange={handleChangeSecondaryMetric}
                isMulti={true}
              />
            </Box>
          </Grid>
        </Grid>

        <Grid
          container
          item
          spacing={2}
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: "#000",
            display: "grid",
            gridTemplateColumns: { md: "1fr 1fr 1fr 1fr 1fr" },
            gap: 2,
          }}
        >
          <Grid item xs={12}>
            <Box
              sx={{
                // p: 2,
                borderRadius: 2,
                bgcolor: "#000",
                // display: 'grid',
                // gridTemplateColumns: { md: '1fr 1fr' },
                // gap: 2,
              }}
            >
              {/* Select Date: */}
              <>
                <label htmlFor="date-picker-range">Date Range:</label>
                <RangePicker
                  id="date-picker-range"
                  size="midle"
                  className="ant-date-picker-input"
                  pickerStyle={{ backgroundColor: "#000", color: "#fff" }} // Add
                  onCalendarChange={handleChangeDate}
                  defaultValue={[
                    dayjs(graphDate.startDate, graphDateFormat),
                    dayjs(graphDate.endDate, graphDateFormat),
                  ]}
                  // style={darkDatePickerStyle}
                />
              </>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                // p: 2,
                borderRadius: 2,
                bgcolor: "#000",
                // display: 'grid',
                // gridTemplateColumns: { md: '1fr 1fr' },
                // gap: 2,
              }}
            >
              {/* Select Date: */}
              <SelectBox
                options={primaryScaleList}
                defaultValue={[primayScale]}
                isDisabled={false}
                isLoading={false}
                isClearable={false}
                isSearchable={false}
                handleSelectChange={handleScaleChangePrimary}
                id="primary-scale"
                label="Scale"
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                // p: 2,
                borderRadius: 2,
                bgcolor: "#000",
                // display: 'grid',
                // gridTemplateColumns: { md: '1fr 1fr' },
                // gap: 2,
              }}
            >
              {/* Select Date: */}
              <SelectBox
                options={primaryScaleList}
                defaultValue={[secondaryScale]}
                isDisabled={false}
                isLoading={false}
                isClearable={false}
                isSearchable={false}
                handleSelectChange={handleScaleChangeSecondary}
                id="secondary-scale"
                label="Scale"
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                // p: 2,
                borderRadius: 2,
                bgcolor: "#000",
                // display: 'grid',
                // gridTemplateColumns: { md: '1fr 1fr' },
                // gap: 2,
              }}
            >
              {/* Select Date: */}
              <RadioItem />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                borderRadius: 2,
                bgcolor: "#000",
                cursor: "pointer",
                "&:hover": {
                  bgcolor: "#333", // Background color on hover
                },
              }}
              onClick={handleClickShowIndicatorModal}
            >
              <Tooltip
                placement="right-start"
                arrow
                title="Add Indicator"
                sx={{
                  "&:hover": {
                    bgcolor: "#333",
                  },
                }}
              >
                <ShowChartIcon iconposition="end" />
                Indicators
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
        <Grid
          container
          item
          spacing={2}
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: "#000",
            display: "grid",
          }}
        >
          {showLoader && (
              <div className="loader-container">
                {" "}
                <PreLoader />{" "}
              </div>
          )}
          <Box
            id="chart-id"
            ref={chartContainerRef}
            className="chart-container"
            sx={{ height: "75vh", position: "relative" }}
          >
            <div
              ref={tooltipRef}
              style={{
                position: "absolute",
                zIndex: "100",
                width: "120px",
                height: "75vh",
                display: "none",
              }}
              className="graph-tooltip"
            >
              this is the toopltip value: 2000
            </div>
            <Box sx={{ position: "relative", zIndex: "10", fontSize: "13px" }}>
              <Box
                id="primary-legend-div"
                sx={{ position: "absolute", left: "70px" }}
              >
                {selectPrimaryMetrics?.map((metric, index) => (
                  <Box key={metric.value}>
                    {metric.value?.split(" - ")[0]}{" "}
                    {hoverdValuesLegends.length
                      ? `: ${
                          hoverdValuesLegends[
                            index + selectSecondayMetrics?.length
                          ]
                        }`
                      : null}
                  </Box>
                ))}

                {Object.keys(selectedIndicatorsList)?.map((indicator) => (
                  <Box key={indicator} onClick={handleClickOpenIndicatorSettingsModal}>
                    {indicator}
                  </Box>
                ))}
              </Box>

              <Box
                id="secondary-legend-div"
                sx={{ position: "relative", float: "right", right: "100px" }}
              >
                {selectSecondayMetrics?.map((metric, index) => (
                  <Box key={metric.value}>
                    {metric.value}{" "}
                    {hoverdValuesLegends.length
                      ? `: ${hoverdValuesLegends[index]}`
                      : null}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Grid>
      </ThemeProvider>
    </>
  );
}
